import { Box, Typography, Button } from "@mui/material";
import { Form as RBFORM } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import PhoneInput from "react-phone-input-2";
import { FieldArray, Formik, Form, Field, ErrorMessage } from 'formik';
import Grid from "@mui/material/Grid2";
import Stack from '@mui/material/Stack';
import { ToastContainer } from "react-toastify";
import React, { useState, useEffect, useRef } from "react";
import { array, object, number, string, boolean, ValidationError, mixed, ref } from 'yup';
import { TextField } from 'formik-material-ui';
import { getCountriesAPI, getStatesAPI, getCitiesAPI } from "../../../../services/placeService";
import { postMethodFormData, getMethod, postMethod } from "../../../../services/apiService";
import { ToastContext, useToastContext } from "../../../../context/toastContext";
import { useNavigate } from "react-router-dom";
import { forwardRef } from "react";
import { isValidPhoneNumber } from 'libphonenumber-js';
import { allTimezones } from "react-timezone-select";
import { useImperativeHandle } from "react";

interface InitialValues {
    platformKey: string;
    name: string;
    email: string;
    addressline1: string;
    addressline2: string;
    postalcode: string;
    city: string | null;
    state: string | null;
    country: string | null;
    mobile: string;
    timezone: string;
    latitude: string;
    longitude: string;
}

interface PlatformDetailsProps {
    onPlatformTypeSetupConfigLoad: (config: any) => void;
}

const PlatformDetails = ({ onPlatformTypeSetupConfigLoad }: PlatformDetailsProps) => {

    const { setToast } = useToastContext() as ToastContext;
    const [initialValues, setInitialValues] = useState<InitialValues>({
        platformKey: '',
        name: '',
        email: '',
        addressline1: '',
        addressline2: '',
        postalcode: '',
        city: '',
        state: '',
        country: '',
        mobile: '',
        timezone: '',
        latitude: "0.0",
        longitude: "0.0",
    });

    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [filteredStateList, setFilteredStateList] = useState([]);
    const [filteredCityList, setFilteredCityList] = useState([]);
    const [platformMobileNumber, setPlatformMobileNumber] = useState("");
    const [country, setCountry] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");


    const getCountryList = async () => {
        const resp = await getCountriesAPI();
        setCountryList(resp.countries);
    }

    const getStateList = async () => {
        const resp = await getStatesAPI();
        setStateList(resp.states);
    }

    const getCityList = async () => {
        const resp = await getCitiesAPI();
        setCityList(resp.cities);
    }

    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        getCountryList();
        getStateList();
        getCityList();
        // getPlatformDetails();
    }, []);

    useEffect(() => {
        if (countryList.length > 0 && stateList.length > 0 && cityList.length > 0)
            getPlatformDetails();
    }, [countryList, stateList, cityList])

    const getPlatformDetails = async () => {
        const resp = await getMethod(`platform/license`);
       
        if (resp?.data?.code === 200 && resp?.data?.result?.[0]) {
            const data = resp?.data?.result[0];
            let country_id = "";
            if (data?.address?.country !== "") {
                country_id = await getCountryId(data?.address?.country);
            }
            if (country_id) {
                applyCountrySelection(country_id.toString());
            }
            let state_id = "";
            if (data?.address?.state !== "" && country_id != "") {
                state_id = await getStateId(data?.address?.state, country_id.toString());
            }
            if (state_id) {
                applyStateSelection(state_id.toString());
            }

            let city_id = "";
            if (data?.address?.city !== "" && state_id != "") {
                city_id = await getCityId(data?.address?.city, state_id.toString());
            }
            setInitialValues({
                platformKey: data?.platformKey || "",
                name: data?.name || "",
                email: data?.email || "",
                addressline1: data?.address?.addressline1 || "",
                addressline2: data?.address?.addressline2 || "",
                postalcode: data?.address?.postalcode || "",
                city: city_id,
                state: state_id,
                country: country_id,
                mobile: data?.mobile || "",
                timezone: data?.timezone || "",
                latitude: data?.latitude || "0.0",
                longitude: data?.longitude || "0.0",
            });
            onPlatformTypeSetupConfigLoad(data?.platformType);
        }
    };

    const validateMobileNumber = async (mobile_number: any) => {
        if (!mobile_number) {
            setPlatformMobileNumber("");
            return;
        }

        if (mobile_number && !mobile_number.includes('+')) {
            mobile_number = `+${mobile_number}`;
        }

        const isValid = isValidPhoneNumber(mobile_number);
        if (!isValid) {
            setPlatformMobileNumber("Enter a valid mobile number!");

        } else {
            setPlatformMobileNumber("");
        }
    };

    //For TimeZone
    function getTimezoneOffset(zone: string) {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: zone,
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
        });

        const parts = formatter.formatToParts(now);
        const localHour = Number(parts.find((p) => p.type === "hour")?.value);
        const localMinute = Number(parts.find((p) => p.type === "minute")?.value);

        const utcHour = now.getUTCHours();
        const utcMinute = now.getUTCMinutes();

        const totalMinutes = (localHour * 60 + localMinute) - (utcHour * 60 + utcMinute);
        return totalMinutes;
    }

    function formatOffset(minutes: number) {
        const sign = minutes >= 0 ? "+" : "-";
        const abs = Math.abs(minutes);
        const hours = String(Math.floor(abs / 60)).padStart(2, "0");
        const mins = String(abs % 60).padStart(2, "0");
        return `UTC${sign}${hours}:${mins}`;
    }

    const timezoneList = Object.keys(allTimezones).map((zone) => {
        const offsetInMinutes = getTimezoneOffset(zone);
        const formatted = formatOffset(offsetInMinutes);

        return {
            label: `(${formatted}) ${zone}`,
            value: zone,
        };
    });

    const handleCountryChange = async (e: any, setFieldValue: any) => {
        const selectedCountryId = e.target.value;
        const selectedCountry = countryList.find((country: any) => country.id.toString() === selectedCountryId) || { id: '', name: '', shortname: '' };
        // setCountry(selectedCountry.id === '' || selectedCountry.id === '0' ? '' : selectedCountry.name);
        setFieldValue("country", selectedCountry.id.toString());
        applyCountrySelection(selectedCountryId);

        // setTimeout(() => {
        //     const filteredStates = stateList.filter((state: any) => state.country_id.toString() === selectedCountry.id.toString());
        //     setFilteredStateList(filteredStates);
        // }, 200);
    }


    const handleStateChange = async (e: any, setFieldValue: any) => {
        const selectedStateId = e.target.value;
        const selectedState = stateList.find((state: any) => state.id.toString() === selectedStateId) || { id: '', name: '', country_id: '' };
        // setState(selectedState.id === '' || selectedState.id === '0' ? '' : selectedState.name);
        setFieldValue("state", selectedState.id.toString());
        applyStateSelection(selectedStateId);
        // setTimeout(() => {
        //     const filteredCities = cityList.filter((city: any) => city.state_id.toString() === selectedState.id.toString());
        //     setFilteredCityList(filteredCities);
        // }, 200);
    }


    const handleCityChange = async (e: any, setFieldValue: any) => {
        const selectedCityId = e.target.value;
        const selectedCity = cityList.find((country: any) => country.id.toString() === selectedCityId) || { id: '', name: '', state_id: '' };
        setCity(selectedCity.id === '' || selectedCity.id === '0' ? '' : selectedCity.name);
        setFieldValue("city", selectedCity.id.toString());
    }

    const getCountryId = async (country_name: string) => {
        const country: any = countryList.find((country: any) => country.name === country_name);
        if (country !== undefined) {
            setCountry(country.name);
            return country.id;
        }
        else return "";
    }

    const getStateId = async (state_name: string, country_id: string) => {
        const f_statelist = stateList?.filter((item: any) => item.country_id === country_id);
        setFilteredStateList(f_statelist);
        const state: any = f_statelist.find((state: any) => state.name === state_name && state.country_id === country_id);
        if (state !== undefined) {
            setState(state.name);
            return state.id;
        }
        else return "";
    }

    const getCityId = async (city_name: string, state_id: string) => {
        const f_citylist = cityList?.filter((item: any) => item.state_id === state_id);
        setFilteredCityList(f_citylist);
        const city: any = cityList.find((city: any) => city.name === city_name && city.state_id === state_id);
        if (city !== undefined) {
            setCity(city.name);
            return city.id;
        }
        else return "";
    }

    const applyCountrySelection = (countryId: string) => {
        const selectedCountry = countryList.find(
            (country: any) => country.id.toString() === countryId.toString()
        ) || { id: '', name: '', shortname: '' };

        setCountry(
            selectedCountry.id === '' || selectedCountry.id === '0'
                ? ''
                : selectedCountry.name
        );

        const filteredStates = stateList.filter(
            (state: any) =>
                state.country_id.toString() === selectedCountry.id.toString()
        );

        setFilteredStateList(filteredStates);
        setFilteredCityList([]);
    };

    const applyStateSelection = (stateId: string) => {
        const selectedState = stateList.find(
            (state: any) => state.id.toString() === stateId.toString()
        ) || { id: '', name: '', shortname: '' };

        setState(
            selectedState.id === '' || selectedState.id === '0'
                ? ''
                : selectedState.name
        );

        const filteredCities = cityList.filter(
            (city: any) =>
                city.state_id.toString() === selectedState.id.toString()
        );

        setFilteredCityList(filteredCities);
    };


    return (
        <Box>

            <Formik

                initialValues={initialValues}
                enableReinitialize={true} // Enable reinitialization
                validationSchema={object({
                    name: string().trim().required("Name is required!"),
                    email: string().email("Valid Email ID is required!").required("Email ID is required!"),
                    addressline1: string()
                        .trim()
                        .max(255, "Maximum length reached!")
                        .nullable()
                        .optional(),
                    addressline2: string()
                        .trim()
                        .max(255, "Maximum length reached!")
                        .nullable()
                        .optional(),
                    latitude: number()
                        .typeError("Latitude must be a valid number")
                        .min(-90, "Latitude must be greater than or equal to -90")
                        .max(90, "Latitude must be less than or equal to 90")
                        .nullable()
                        .transform((value, originalValue) =>
                            originalValue === "" ? null : value
                        ),
                    longitude: number()
                        .typeError("Longitude must be a valid number")
                        .min(-180, "Longitude must be greater than or equal to -180")
                        .max(180, "Longitude must be less than or equal to 180")
                        .nullable()
                        .transform((value, originalValue) =>
                            originalValue === "" ? null : value
                        ),

                    postalcode: string().trim().matches(/^\d{4,8}$/, "Postal Code must be 4 to 8 digits"),
       
                })}

                onSubmit={async (values) => {

                    if (platformMobileNumber) {
                        return;
                    }

                    const payload = {
                        platformKey: values.platformKey,
                        name: values.name,
                        email: values.email,
                        mobile: values.mobile,
                        timezone: values.timezone,
                        latitude: values.latitude,
                        longitude: values.longitude,

                        address: {
                            addressline1: values.addressline1,
                            addressline2: values.addressline2,
                            city: city,
                            state: state,
                            country: country,
                            postalcode: values.postalcode
                        }
                    };

                    const resp = await postMethod("platform/update", payload);
                    if (resp?.status === 200) {
                        setToast("Sucessfully Updated", "success");
                    } else {
                        setToast(resp?.data?.message, "error");
                    }
                }}
            >

                {({ values, errors, isSubmitting, isValid, setFieldValue, setFieldTouched, touched, submitForm }) => {

                    // if (onSubmitRequest) {
                    //     onSubmitRequest(submitForm); 
                    // }

                    return (

                        <React.Fragment>
                            <ToastContainer />
                            {/* {JSON.stringify(values, null, 2)} */}
                            <Form autoComplete="off">
                                <Row className="mb-3">
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Name<span style={{ color: "red" }}>*</span></RBFORM.Label>
                                                <Field
                                                    name="name"
                                                    placeholder="Enter name"
                                                    component={TextField}
                                                    className="inputC"
                                                    onChange={(e: any) => {
                                                        setFieldValue("name", e.target.value);
                                                    }}
                                                />
                                            </RBFORM.Group>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Email<span style={{ color: "red" }}>*</span></RBFORM.Label>
                                                <Field
                                                    name="email"
                                                    placeholder="Enter Email ID"
                                                    component={TextField}
                                                    className="inputC"
                                                    onChange={(e: any) => {
                                                        setFieldValue("email", e.target.value);
                                                    }}
                                                />
                                            </RBFORM.Group>
                                        </Grid>
                                    </Grid>
                                </Row>

                                <hr
                                    style={{
                                        border: "none",
                                        height: "1px",
                                        color: "#313838",
                                        backgroundColor: "#313838",
                                        margin: "8px 0 30px 0",
                                    }}
                                />


                                <Row className="mb-3">
                                    <Grid container columnSpacing={3} rowSpacing={0}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Address Line 1</RBFORM.Label>
                                                <Field
                                                    name="addressline1"
                                                    placeholder="Street address / company name / c/o"
                                                    component={TextField}
                                                    className="inputC"
                                                    inputProps={{ maxLength: 255 }}
                                                    onChange={(e: any) => {
                                                        setFieldValue("addressline1", e.target.value);
                                                    }}
                                                />
                                            </RBFORM.Group>
                                        </Grid>


                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Address Line 2</RBFORM.Label>
                                                <Field
                                                    name="addressline2"
                                                    placeholder="Street address / company name / c/o"
                                                    component={TextField}
                                                    className="inputC"
                                                    inputProps={{ maxLength: 255 }}
                                                    onChange={(e: any) => {
                                                        setFieldValue("addressline2", e.target.value);
                                                    }}
                                                />
                                            </RBFORM.Group>
                                        </Grid>


                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Country</RBFORM.Label>
                                                <Field
                                                    name="country"
                                                    as="select"
                                                    className="inputC custDropdown"
                                                    onChange={(e: any) => handleCountryChange(e, setFieldValue)}
                                                >
                                                    {/* <option>Select Country</option>
                                                    {countryList?.length > 0 && (
                                                        countryList.map((country: any) => (
                                                            <option key={country.id} value={country.id}>
                                                                {country.name}
                                                            </option>
                                                        ))
                                                    )} */}
                                                    {countryList?.length > 0 ? (
                                                        countryList.map((country: any) => (
                                                            <option key={country.id} value={country.id}>
                                                                {country.name}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option>Select Country</option>
                                                    )}
                                                </Field>
                                            </RBFORM.Group>
                                        </Grid>


                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">State</RBFORM.Label>
                                                <Field
                                                    name="state"
                                                    as="select"
                                                    className="inputC custDropdown"
                                                    onChange={(e: any) => handleStateChange(e, setFieldValue)}
                                                >
                                                    <option>Select State</option>
                                                    {filteredStateList?.length > 0 && (
                                                        filteredStateList.map((state: any) => (
                                                            <option key={state.id} value={state.id}>
                                                                {state.name}
                                                            </option>
                                                        ))
                                                    )}
                                                </Field>
                                            </RBFORM.Group>
                                        </Grid>


                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">City</RBFORM.Label>
                                                <Field
                                                    name="city"
                                                    as="select"
                                                    placeholder="City"
                                                    className="inputC custDropdown"
                                                    onChange={(e: any) => handleCityChange(e, setFieldValue)}
                                                >
                                                    <option>Select City</option>
                                                    {filteredCityList?.length > 0 && (
                                                        filteredCityList.map((city: any) => (
                                                            <option key={city.id} value={city.id}>
                                                                {city.name}
                                                            </option>
                                                        ))
                                                    )}
                                                </Field>
                                            </RBFORM.Group>
                                        </Grid>


                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Postal Code</RBFORM.Label>
                                                <Field
                                                    name="postalcode"
                                                    placeholder="Enter Postal Code"
                                                    component={TextField}
                                                    className="inputC"
                                                    inputProps={{
                                                        maxLength: 8,
                                                        inputMode: "numeric",
                                                        pattern: "[0-9]*",
                                                    }}
                                                    onChange={(e: any) => {
                                                        const value = e.target.value.replace(/\D/g, "");
                                                        setFieldValue("postalcode", value);
                                                    }}
                                                />
                                            </RBFORM.Group>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Mobile Number</RBFORM.Label>
                                                <PhoneInput
                                                    specialLabel=""
                                                    dropdownClass="user-phone-input-dropdown"
                                                    containerClass="user-phone-input-container"
                                                    inputClass={`modalForm-input user-phone-input ${errors.mobile && touched.mobile ? "errors" : ""}`}
                                                    inputStyle={{
                                                        boxShadow: 'none',
                                                        color: "#A5A8A9",
                                                        border: errors.mobile && touched.mobile ? "1px solid red" : "1px solid #313838",
                                                        width: "100%",
                                                        height: "40px",
                                                        backgroundColor: "#192529",
                                                        opacity: values.mobile ? 1 : 0.5
                                                    }}
                                                    inputProps={{ maxLength: 20 }}
                                                    value={values.mobile}
                                                    onChange={(number) => {
                                                        const formattedNumber = number.startsWith('+') ? number : `+${number}`;
                                                        
                                                        if(formattedNumber == "+")
                                                        setFieldValue("mobile", "");
                                                    else
                                                        setFieldValue("mobile", formattedNumber);
                                                        validateMobileNumber(number);
                                                    }}
                                                    onBlur={() => setFieldTouched("mobile", true)}
                                                    // disabled={props?.customerInfo}
                                                    enableLongNumbers={true}
                                                />
                                                {platformMobileNumber && (
                                                    <div className="error-message" style={{ color: "#f44336" }}>{platformMobileNumber}</div>
                                                )}
                                            </RBFORM.Group>
                                        </Grid>

                                        <Grid size={{ xs: 12 }} sx={{ mt: 2 }} />

                                        <Grid size={{ xs: 12 }}>
                                            <hr
                                                style={{
                                                    border: "none",
                                                    height: "1px",
                                                    color: "#313838",
                                                    backgroundColor: "#313838",
                                                    margin: "8px 0 30px 0",
                                                }}
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Time Zone</RBFORM.Label>
                                                <Field
                                                    name="timezone"
                                                    as="select"
                                                    className="inputC custDropdown"
                                                    onChange={(e: any) => setFieldValue("timezone", e.target.value)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <option value="" disabled>Select Time Zone</option>
                                                    {timezoneList.map((tz) => (
                                                        <option key={tz.value} value={tz.value}>
                                                            {tz.label}
                                                        </option>
                                                    ))}

                                                </Field>
                                            </RBFORM.Group>
                                        </Grid>

                                        <Grid size={{ xs: 12 }} />

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Latitude</RBFORM.Label>
                                                <Field
                                                    name="latitude"
                                                    placeholder="Enter Latitude"
                                                    component={TextField}
                                                    type="text"
                                                    inputProps={{ maxLength: 20 }}
                                                    className="inputC"
                                                    onChange={(e: any) => {
                                                        const value = e.target.value;
                                                        // Allow only digits, one decimal point and optional leading minus
                                                        const cleanedValue = value.replace(/[^0-9.-]/g, "");
                                                        setFieldValue("latitude", cleanedValue, true);
                                                    }}
                                                    onBlur={(e: any) => {
                                                        setFieldTouched("latitude", true);

                                                        // If user leaves field empty → set 0.0
                                                        if (e.target.value === "") {
                                                            setFieldValue("latitude", "0.0", true);
                                                        }
                                                    }}
                                                />
                                            </RBFORM.Group>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <RBFORM.Group>
                                                <RBFORM.Label className="label">Longitude</RBFORM.Label>
                                                <Field
                                                    name="longitude"
                                                    placeholder="Enter Longtitude"
                                                    component={TextField}
                                                    type="text"
                                                    inputProps={{ maxLength: 20 }}
                                                    className="inputC"
                                                    onChange={(e: any) => {
                                                        const value = e.target.value;
                                                        // Allow only digits, one decimal point and optional leading minus
                                                        const cleanedValue = value.replace(/[^0-9.-]/g, "");
                                                        setFieldValue("longitude", cleanedValue, true);
                                                    }}
                                                    onBlur={(e: any) => {
                                                        setFieldTouched("longitude", true);

                                                        // If user leaves field empty → set 0.0
                                                        if (e.target.value === "") {
                                                            setFieldValue("longitude", "0.0", true);
                                                        }
                                                    }}
                                                />
                                            </RBFORM.Group>
                                        </Grid>
                                    </Grid>
                                </Row>
                            </Form>
                            <Box
                                sx={{
                                    // backgroundColor: "#192529",
                                    borderBottomLeftRadius: "12px",
                                    borderBottomRightRadius: "12px",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    px: 3,
                                    py: 2,
                                    position: "sticky",
                                    bottom: 0,
                                }}
                            >
                                <Button
                                    type="button"
                                    variant="contained"
                                    className="btnBlue"
                                    sx={{
                                        color: "#1F2F34",
                                        height: "41px",
                                        lineHeight: "41px",
                                        borderColor: "#0B4A62",
                                        background: "#0B4A62",
                                        "&:hover": {
                                            background: "#0E607D",
                                        },
                                        borderRadius: '30px',
                                        fontWeight: '500',
                                        textTransform: "none",
                                    }}
                                    onClick={() => {
                                        submitForm();
                                    }}
                                >
                                    Update
                                </Button>
                            </Box>
                            {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
                        </React.Fragment>
                    );
                }}
            </Formik >
        </Box>
    );
};

export default PlatformDetails;
